import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';

import { DATE_SIMPLE } from '@cast/constants';
import {
  Column,
  ColumnModel,
  DataTable,
  Icons,
  TableHeader,
} from '@cast/design-system';
import { sumDataTransferCost, sumDataTransferTraffic } from '@cast/utils';

import { ExportFileButton } from 'components/buttons';
import { Money } from 'components/money';
import { ExpandedTable } from 'features/cost-report/components/network-cost/ExpandedTable';
import { TrafficCell } from 'features/cost-report/components/network-cost/TrafficCell';
import { convertChartDatapointToCostReportData } from 'features/cost-report/utils/networkCosts';

import { DataTransferTimeSeries } from '../../../../cost-report/types/networkCostOverTime';
import { TimeSeries } from 'types/metrics';
import { getNetworkCostPrecision } from '../_utils';

type DataTransfer = DataTransferTimeSeries[number];

type NetworkDailyDetailsTableProps = {
  chartData: DataTransferTimeSeries | TimeSeries<object>;
  isLoading: boolean;
  timezone: string;
  cacheKey: string;
  urlKey?: string;
  testId: string;
  onDownload?: () => void;
};

export const NetworkDailyDetailsTable = ({
  chartData,
  isLoading,
  timezone,
  cacheKey,
  urlKey,
  testId,
  onDownload,
}: NetworkDailyDetailsTableProps) => {
  const data = convertChartDatapointToCostReportData(chartData);
  const tableData = orderBy(data, 'timestamp', 'desc');

  return (
    <DataTable
      data={tableData}
      isLoading={isLoading}
      maxRows={31}
      rowKey="timestamp"
      testId={testId}
      cacheKey={cacheKey}
      urlKey={urlKey}
      maxHeight="80vh"
      sx={{
        marginTop: 10,
        '& .DS-Table-CellWrapper-expander svg': {
          height: '16px',
          width: '16px',
        },
      }}
      getExpandedPanel={(row, rowState) => (
        <ExpandedTable
          data={row}
          columnModels={
            rowState.tableApi.columnsInfo.columnModels as ColumnModel[]
          }
        />
      )}
      expanderIcon={(_, rowState) =>
        rowState?.isExpanded ? Icons.CaretUp : Icons.CaretDown
      }
      onRowClick={(_event, rowState) => {
        if (rowState.level === 0) {
          rowState.tableApi.toggleIsRowExpanded(rowState.row);
        }
      }}
      outerHeader={
        <TableHeader
          recordsCount="Daily network details"
          recordsCountWrapperProps={{ typography: 'A2' }}
          showSettings={false}
          actions={
            onDownload ? (
              <ExportFileButton onClick={onDownload} testId="download-csv" />
            ) : undefined
          }
        />
      }
    >
      <Column<DataTransfer>
        id="main"
        minWidth={150}
        header="Date"
        renderCell={({ timestamp }) =>
          dayjs.utc(timestamp).tz(timezone).format(DATE_SIMPLE)
        }
        accessor="timestamp"
        sortable
      />

      <Column<DataTransfer>
        id="totalBytes"
        minWidth={360}
        header="Total Traffic"
        renderCell={(row) => {
          const egress = sumDataTransferTraffic(row.egress);
          const ingress = sumDataTransferTraffic(row.ingress);
          return <TrafficCell egress={egress} ingress={ingress} />;
        }}
        accessor={(row) => {
          const egress = sumDataTransferTraffic(row.egress);
          const ingress = sumDataTransferTraffic(row.ingress);
          return egress + ingress;
        }}
        sortable
      />

      <Column<DataTransfer>
        id="totalCost"
        minWidth={360}
        header="Total Cost"
        infoTooltip="Sum of egress and ingress traffic cost. Ingress does not incur any costs, depending on the cloud provider"
        bodyCellSx={{ fontWeight: 'bold' }}
        renderCell={(row: DataTransferTimeSeries[number]) => {
          const egress = sumDataTransferCost(row.egress);
          const ingress = sumDataTransferCost(row.ingress);
          const total = egress + ingress;
          const precision = getNetworkCostPrecision(total);
          return (
            <Money amount={total} precision={precision} fixed={precision} />
          );
        }}
        accessor={(row: DataTransferTimeSeries[number]) => {
          const egress = sumDataTransferCost(row.egress);
          const ingress = sumDataTransferCost(row.ingress);
          return egress + ingress;
        }}
        sortable
      />
    </DataTable>
  );
};
